import { makeAutoObservable, runInAction } from "mobx";
import SessionStore from "./SessionStore";
import { ContactGroup } from "../api/firebase/types/contact.types";
import { Unsubscribe } from "firebase/auth";
import { FirebaseError } from "firebase/app";
import {
  addContactGroup,
  deleteContactGroup,
  removeContactsFromGroup,
  subscribeContactGroups,
  updateContactGroup,
} from "../api/firebase/collections/contactGroupCollection";

class ContactGroupStore {
  error?: any = null;
  isPending: boolean = false;
  contactGroups: ContactGroup[] = [];
  currentContactGroupId?: string;
  unsubscribeContactGroups?: Unsubscribe;

  constructor() {
    makeAutoObservable(this);
  }

  subscribeContactGroups() {
    const onNewSnapshot = (contactGroups: ContactGroup[]) => {
      runInAction(() => {
        this.contactGroups = contactGroups;
      });
    };
    const onError = (error: FirebaseError) => {
      console.error(error);
      runInAction(() => {
        this.error = error;
      });
    };
    const unsubscribe = subscribeContactGroups(onNewSnapshot, onError);
    runInAction(() => {
      this.unsubscribeContactGroups = unsubscribe;
    });
  }

  setCurrentContactGroup = (contactGroupId?: string) => {
    runInAction(() => {
      this.currentContactGroupId = contactGroupId;
    });
  };

  addContactGroup = async (
    contactGroup: Omit<ContactGroup, "id" | "userId">
  ) => {
    runInAction(() => {
      this.isPending = true;
      this.error = undefined;
    });
    try {
      const newContactGroup: Omit<ContactGroup, "id"> = {
        ...contactGroup,
        userId: SessionStore.user!.uid,
      };
      await addContactGroup(newContactGroup);
      runInAction(() => {
        this.isPending = false;
      });
      return true;
    } catch (err) {
      runInAction(() => {
        this.isPending = false;
        this.error = err;
      });
      return false;
    }
  };

  updateContactGroup = async (
    updatedContactGroup: Omit<ContactGroup, "id" | "userId">
  ) => {
    runInAction(() => {
      this.isPending = true;
      this.error = undefined;
    });
    try {
      await updateContactGroup({
        ...updatedContactGroup,
        id: this.currentContactGroupId!,
        userId: SessionStore.user!.uid,
      });
      runInAction(() => {
        this.isPending = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isPending = false;
        this.error = err;
      });
    }
  };

  deleteCurrentContactGroup = async () => {
    if (!this.currentContactGroupId)
      throw new Error("Es ist keine Kontaktgruppe ausgewählt");
    runInAction(() => {
      this.isPending = true;
      this.error = undefined;
    });
    try {
      await deleteContactGroup(this.currentContactGroupId);
      runInAction(() => {
        this.isPending = false;
        this.currentContactGroupId = undefined;
      });
      return true;
    } catch (err) {
      runInAction(() => {
        this.isPending = false;
        this.error = err;
      });
      return false;
    }
  };

  deleteContactsFromGroups = async (contactIdsToRemove: string[]) => {
    runInAction(() => {
      this.isPending = true;
      this.error = undefined;
    });
    try {
      this.contactGroups.forEach((group) => {
        removeContactsFromGroup(group.id, contactIdsToRemove);
      });
      runInAction(() => {
        this.isPending = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isPending = false;
        this.error = err;
      });
    }
  };

  getContactGroupsById = (contactGroupIds?: string[]) => {
    if (!contactGroupIds) return [];
    const contactsToNotify = this.contactGroups?.filter((contactGroup) => {
      return contactGroupIds.includes(contactGroup.id);
    });
    return contactsToNotify;
  };
}

export default new ContactGroupStore();
